

import React, { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { FaEdit, FaSave, FaTimes } from 'react-icons/fa';
import { MdBlock } from 'react-icons/md';

const GET_ALL_CUSTOMERS = gql`
  query GetAllCustomers {
    getAllCustomers {
      customer_id
      customer_name
      customer_email
      customer_phonenumber
      isdeactivated
    }
  }
`;

const UPDATE_CUSTOMER = gql`
  mutation UpdateCustomer($id: ID!, $name: String, $email: String, $phoneNumber: String) {
    updateCustomer(id: $id, name: $name, email: $email, phoneNumber: $phoneNumber) {
      customer_id
      customer_name
      customer_email
      customer_phonenumber
    }
  }
`;

const TOGGLE_DEACTIVATION = gql`
  mutation ToggleDeactivation($id: ID!, $isdeactivated: Boolean!) {
    toggleDeactivation(id: $id, isdeactivated: $isdeactivated) {
      customer_id
      isdeactivated
    }
  }
`;


const CustomerList = () => {
  const [searchTerms, setSearchTerms] = useState({
    customer_id: '',
    customer_name: '',
    customer_email: '',
    customer_phonenumber: '',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [customersPerPage] = useState(10);
  const [editingCustomerId, setEditingCustomerId] = useState(null);
  const [tempCustomerData, setTempCustomerData] = useState({});

  const { loading, error, data } = useQuery(GET_ALL_CUSTOMERS);
  const [updateCustomer] = useMutation(UPDATE_CUSTOMER, {
    update(cache, { data: { updateCustomer } }) {
      const { getAllCustomers } = cache.readQuery({ query: GET_ALL_CUSTOMERS });
      const updatedCustomers = getAllCustomers.map(customer =>
        customer.customer_id === updateCustomer.customer_id ? updateCustomer : customer
      );
      cache.writeQuery({
        query: GET_ALL_CUSTOMERS,
        data: { getAllCustomers: updatedCustomers },
      });
    }
  });
  const [toggleDeactivation] = useMutation(TOGGLE_DEACTIVATION, {
    update(cache, { data: { toggleDeactivation } }) {
      const { getAllCustomers } = cache.readQuery({ query: GET_ALL_CUSTOMERS });
      const updatedCustomers = getAllCustomers.map(customer =>
        customer.customer_id === toggleDeactivation.customer_id
          ? { ...customer, isdeactivated: toggleDeactivation.isdeactivated }
          : customer
      );
      cache.writeQuery({
        query: GET_ALL_CUSTOMERS,
        data: { getAllCustomers: updatedCustomers },
      });
    }
  });

  if (loading) return <div className="text-center py-4">Loading...</div>;
  if (error) return <div className="text-center py-4 text-red-600">Error: {error.message}</div>;

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearchTerms(prev => ({
      ...prev,
      [name]: value || '' // Ensure we don't set undefined
    }));
  };

  const handleEdit = (customer) => {
    setEditingCustomerId(customer.customer_id);
    setTempCustomerData(customer);
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setTempCustomerData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async (customerId) => {
    try {
      await updateCustomer({
        variables: {
          id: customerId,
          name: tempCustomerData.customer_name,
          email: tempCustomerData.customer_email,
          phoneNumber: tempCustomerData.customer_phonenumber
        }
      });
      setEditingCustomerId(null);
    } catch (error) {
      console.error('Error updating customer:', error);
    }
  };

  const handleToggleDeactivate = async (customerId, currentStatus) => {
    try {
      await toggleDeactivation({
        variables: {
          id: customerId,
          isdeactivated: !currentStatus
        }
      });
    } catch (error) {
      console.error('Error toggling deactivation:', error);
    }
  };

  const filteredCustomers = data.getAllCustomers.filter(customer =>
    Object.entries(searchTerms).every(([key, value]) => {
      let customerValue = customer[key];
      if(key==='customer_phonenumber'){
        customerValue=0
      }
      
      return customerValue !== null && customerValue !== undefined && 
             customerValue.toString().toLowerCase().includes(value.toLowerCase());
    })
  );

  const indexOfLastCustomer = currentPage * customersPerPage;
  const indexOfFirstCustomer = indexOfLastCustomer - customersPerPage;
  const currentCustomers = filteredCustomers.slice(indexOfFirstCustomer, indexOfLastCustomer);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const headers = {
    customer_id: 'ID',
    customer_name: 'Name',
    customer_email: 'Email',
    customer_phonenumber: 'Phone',
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-4">Customer List</h1>
      <div className="overflow-x-auto shadow-md sm:rounded-lg">
        <table className="w-full text-sm text-left text-gray-900">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50">
            <tr>
              {Object.values(headers).map((header) => (
                <th key={header} className="px-6 py-3">{header}</th>
              ))}
              <th className="px-6 py-3">Actions</th>
            </tr>
            <tr>
              {Object.keys(headers).map((key) => (
                <th key={key} className="px-6 py-3">
                  <input
                    type="text"
                    name={key}
                    className="w-full px-2 py-1 text-sm border rounded"
                    placeholder={`Search ${headers[key]}`}
                    value={searchTerms[key]}
                    onChange={handleSearchChange}
                  />
                </th>
              ))}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {currentCustomers.map(customer => (
              <tr key={customer.customer_id} className={`bg-white border-b ${customer.isdeactivated ? 'bg-red-100' : ''}`}>
                {Object.keys(headers).map((key) => (
                  <td key={key} className="px-6 py-4 whitespace-nowrap">
                    {editingCustomerId === customer.customer_id ? (
                      <input
                        type="text"
                        name={key}
                        value={tempCustomerData[key]}
                        onChange={handleFieldChange}
                        className="w-full px-2 py-1 text-sm border rounded"
                      />
                    ) : (
                      customer[key]
                    )}
                  </td>
                ))}
                <td className="px-6 py-4 whitespace-nowrap">
                  {editingCustomerId === customer.customer_id ? (
                    <>
                      <button onClick={() => handleSave(customer.customer_id)} className="text-green-600 hover:text-green-900 mr-2">
                        <FaSave />
                      </button>
                      <button onClick={() => setEditingCustomerId(null)} className="text-gray-600 hover:text-gray-900">
                        <FaTimes />
                      </button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => handleEdit(customer)} className="text-blue-600 hover:text-blue-900 mr-2">
                        <FaEdit />
                      </button>
                      <button onClick={() => handleToggleDeactivate(customer.customer_id, customer.isdeactivated)} className="text-red-600 ">
                        <MdBlock color={customer.isdeactivated ? "red" : "green"} />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-4 flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{indexOfFirstCustomer + 1}</span> to <span className="font-medium">{Math.min(indexOfLastCustomer, filteredCustomers.length)}</span> of{' '}
                <span className="font-medium">{filteredCustomers.length}</span> results
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                {[...Array(Math.ceil(filteredCustomers.length / customersPerPage))].map((_, index) => (
                  <button
                    key={index}
                    onClick={() => paginate(index + 1)}
                    className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                      currentPage === index + 1
                        ? 'z-10 bg-indigo-600 text-white'
                        : 'text-gray-900 ring-1 ring-inset ring-gray-300'
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Wrap the CustomerList in the ErrorBoundary
export default CustomerList;
